import React, { memo, useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory, useLocation } from 'react-router-dom';
import moment from 'moment';
import { Button, Spin, Alert, Dropdown, Menu, Tabs, Collapse } from 'antd';

import { MainTemplate } from 'templates';
import { WeeklyLiquidityChart } from 'organisms';
import { FakturowniaSyncChip, IExpenseFormPopupData } from 'molecules';
import {
  useToasts,
  isFakturowniaPermissionDenied,
  usePopupStateOperations,
  useExpenseMutations,
} from 'core/hooks';
import { GlobalToastTypeNames, ToastTypes } from 'types';
import {
  usePaymentCalendarQuery,
  useDeletePlannedPaymentMutation,
  PaymentCalendarRowType,
  PopupNames,
} from 'core';

import * as S from './PaymentCalendar.styled';
import {
  TCalendarDay,
  TCalendarRow,
  TCalendarView,
  rangeForView,
  periodLabel,
  useSetRowStatus,
  parseExpenseRef,
} from './shared';
import { PlannedPaymentModal, IEditableRow } from './parts/PlannedPaymentModal';
import { SettingsDrawer } from './parts/SettingsDrawer';
import { ViewSwitcher } from './parts/ViewSwitcher';
import { SummaryStrip } from './parts/SummaryStrip';
import { AgendaFeed } from './parts/AgendaFeed';
import { PlanningBoard } from './parts/PlanningBoard';
import { SaldoBlock } from './parts/SaldoBlock';
import { CalendarCharts } from './parts/CalendarCharts';
import { ToPayList } from './parts/ToPayList';

const DATE_FMT = 'YYYY-MM-DD';
const DEFAULT_VIEW: TCalendarView = 'thisWeek';

type TPageTab = 'overview' | 'planning';

const todayIso = (): string => moment().format(DATE_FMT);
const mondayOf = (dateIso: string): string =>
  moment(dateIso).startOf('isoWeek').format(DATE_FMT);

const toEditable = (row: PaymentCalendarRowType): IEditableRow => ({
  direction: row.direction || 'expense',
  source: row.source || 'manual',
  kind: row.kind || 'manual',
  title: row.title,
  date: row.date,
  amount: row.amount,
  currency: row.currency,
  comment: row.comment,
  status: row.status,
  plannedPaymentId: row.plannedPaymentId,
  sourceRef: row.sourceRef,
});

/**
 * `/finances/calendar` — the payment-calendar cockpit, two tabs:
 *
 *  - «Обзор» (default): KPI strip (incl. the live «На счёте сейчас» card),
 *    the collapsible weekly liquidity forecast, the horizon switcher and the
 *    read-mostly agenda feed with a finance rail (saldo / chart / to-pay).
 *  - «Планирование»: the 7-column drag-and-drop week board.
 *
 * The active tab lives in the URL (`?tab=planning`, `history.replace` — same
 * pattern as GroupDetail's deep-link state) so it survives a reload. One
 * calendar query serves the active tab: the overview fetches the horizon
 * chosen in the ViewSwitcher, the planning tab fetches the anchor week ± 1
 * week so cross-week drags never lose the dragged row.
 */
export const PaymentCalendar = memo(() => {
  const { t } = useTranslation();
  const { addToast } = useToasts();
  const history = useHistory();
  const location = useLocation();
  const {
    operations: { showPopup },
  } = usePopupStateOperations();
  const { deleteExpense } = useExpenseMutations();

  /* ------------------------------ tab state ------------------------------ */

  const tab: TPageTab = useMemo(
    () =>
      new URLSearchParams(location.search).get('tab') === 'planning'
        ? 'planning'
        : 'overview',
    [location.search],
  );

  const setTab = useCallback(
    (next: string) => {
      const params = new URLSearchParams(location.search);
      if (next === 'planning') params.set('tab', 'planning');
      else params.delete('tab');
      history.replace({ ...location, search: params.toString() });
    },
    [history, location],
  );

  /* --------------------------- overview state ---------------------------- */

  const [view, setView] = useState<TCalendarView>(DEFAULT_VIEW);
  const [anchor, setAnchor] = useState<string>(todayIso);

  /* --------------------------- planning state ---------------------------- */

  const [weekAnchor, setWeekAnchor] = useState<string>(() =>
    mondayOf(todayIso()),
  );

  /* ------------------------------ data layer ----------------------------- */

  // Planning fetches 3 weeks (anchor ± 1) so edge-hover paging and drops onto
  // the ←/→ buttons still resolve the dragged row from the fetched set.
  const range = useMemo(() => {
    if (tab === 'planning') {
      return {
        from: moment(weekAnchor).subtract(7, 'day').format(DATE_FMT),
        to: moment(weekAnchor).add(13, 'day').format(DATE_FMT),
      };
    }
    return rangeForView(view, anchor);
  }, [tab, weekAnchor, view, anchor]);

  const {
    data,
    previousData,
    loading,
    error,
    refetch,
  } = usePaymentCalendarQuery({
    variables: { dateFrom: range.from, dateTo: range.to },
    fetchPolicy: 'cache-and-network',
  });
  const [deleteMutation] = useDeletePlannedPaymentMutation();

  const onRefetch = useCallback(() => {
    refetch();
  }, [refetch]);

  const { setStatus } = useSetRowStatus({ onDone: onRefetch });

  // Keep the previous page while variables change (mid-drag week paging must
  // not blank the board).
  const calendar = (data ?? previousData)?.paymentCalendar;

  const days = useMemo<TCalendarDay[]>(
    () => (calendar?.days || []).filter(Boolean) as TCalendarDay[],
    [calendar],
  );

  const sortedDays = useMemo(
    () => [...days].sort((a, b) => (a.date || '').localeCompare(b.date || '')),
    [days],
  );

  /* --------------------------- overview wiring --------------------------- */

  // Switching view jumps to the CURRENT instance of that view (anchor → today)
  // — a named view like «Сегодня»/«Эта неделя» must always mean the real
  // present period; ←/→ is how the user pages away from today.
  const onViewChange = useCallback((next: TCalendarView) => {
    setAnchor(todayIso());
    setView(next);
  }, []);

  // ←/→ pages the anchor: by ISO week for day/week/sixWeeks, by month for month.
  const onNavigate = useCallback((dir: -1 | 1) => {
    setView((curView) => {
      setAnchor((curAnchor) => {
        const step = curView === 'month' ? 'month' : 'week';
        return moment(curAnchor).add(dir, step).format(DATE_FMT);
      });
      return curView;
    });
  }, []);

  const onToday = useCallback(() => {
    setAnchor(todayIso());
  }, []);

  const label = useMemo(() => periodLabel(view, range, t), [view, range, t]);

  /* ----------------------------- row actions ----------------------------- */

  const [modalRow, setModalRow] = useState<IEditableRow | null>(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);

  // INCOME → the income-only PlannedPaymentModal (create branch).
  const openCreateIncome = useCallback(() => {
    setModalRow(null);
    setModalOpen(true);
  }, []);

  // EXPENSE → the unified ExpenseFormPopup (creates a real `Expense`).
  const openCreateExpense = useCallback(() => {
    showPopup<IExpenseFormPopupData>(PopupNames.expenseForm, {
      prefill: { date: todayIso() },
      onSaved: refetch,
    });
  }, [showPopup, refetch]);

  const onEditRow = useCallback(
    (row: TCalendarRow) => {
      // Expense rows (one-off `expense:{pk}` or recurring occurrence
      // `expense_occurrence:{pk}#date`) edit the underlying Expense series via
      // the unified form. Income / invoice / teacher-payout rows stay on the
      // PlannedPaymentModal.
      const expenseRef = parseExpenseRef(row.sourceRef);
      if (expenseRef) {
        showPopup<IExpenseFormPopupData>(PopupNames.expenseForm, {
          expenseId: expenseRef.pk,
          onSaved: refetch,
        });
        return;
      }
      setModalRow(toEditable(row));
      setModalOpen(true);
    },
    [showPopup, refetch],
  );

  const onDelete = useCallback(
    async (row: PaymentCalendarRowType) => {
      // One-off expense → delete the Expense itself.
      const expenseRef = parseExpenseRef(row.sourceRef);
      if (expenseRef?.kind === 'expense') {
        const ok = await deleteExpense(expenseRef.pk);
        if (ok) refetch();
        return;
      }
      // Stored PlannedPayment (manual/recurring income, materialised override).
      if (!row.plannedPaymentId) return;
      const res = await deleteMutation({
        variables: { id: String(row.plannedPaymentId) },
      });
      const ok = res.data?.deletePlannedPayment?.success;
      addToast({
        data: {
          type: ok ? ToastTypes.success : ToastTypes.error,
          title: ok
            ? t('pages.paymentCalendar.toasts.deleted')
            : t('pages.paymentCalendar.toasts.saveFailed'),
        },
        type: GlobalToastTypeNames.PaymentCalendar,
      });
      if (ok) refetch();
    },
    [deleteExpense, deleteMutation, addToast, t, refetch],
  );

  const onMarkPaid = useCallback(
    (row: TCalendarRow) => {
      setStatus(row, 'paid');
    },
    [setStatus],
  );

  const onSetStatus = useCallback(
    (row: TCalendarRow, status: string) => {
      setStatus(row, status);
    },
    [setStatus],
  );

  /* ----- access / loading guards ----- */
  // Only a real permission rejection is "no access"; transient/network errors
  // get a generic, retryable message instead of masquerading as forbidden.
  if (error) {
    const denied = isFakturowniaPermissionDenied(error);
    return (
      <MainTemplate>
        <S.Wrapper>
          <S.PageTitle>{t('pages.paymentCalendar.title')}</S.PageTitle>
          <S.EmptyState>
            {denied
              ? t('pages.paymentCalendar.noAccess')
              : t('pages.paymentCalendar.loadError')}
          </S.EmptyState>
        </S.Wrapper>
      </MainTemplate>
    );
  }

  const closing = sortedDays.length
    ? sortedDays[sortedDays.length - 1].endBalancePln || 0
    : calendar?.openingBalancePln || 0;

  const overviewBody = (
    <>
      {/* Period summary KPIs (incl. the live balance card). */}
      <S.Card>
        <SummaryStrip
          calendar={calendar}
          periodLabel={label}
          onBalanceChanged={onRefetch}
        />
      </S.Card>

      {/* Weekly liquidity forecast — its own query/horizon; collapsible. */}
      <S.ChartCollapse defaultActiveKey={['chart']}>
        <Collapse.Panel
          key="chart"
          header={t('pages.paymentCalendar.weeklyChart.title')}
        >
          <WeeklyLiquidityChart />
        </Collapse.Panel>
      </S.ChartCollapse>

      {/* Horizon switcher — everything below reacts. */}
      <S.Card>
        <ViewSwitcher
          value={view}
          onChange={onViewChange}
          onNavigate={onNavigate}
          onToday={onToday}
          periodLabel={label}
        />
      </S.Card>

      {loading && !calendar ? (
        <S.CenterLoader>
          <Spin />
        </S.CenterLoader>
      ) : (
        <S.OverviewGrid>
          <S.OverviewMain>
            <AgendaFeed
              days={sortedDays}
              onEdit={onEditRow}
              onDelete={onDelete}
              onMoved={onRefetch}
              onMarkPaid={onMarkPaid}
              onSetStatus={onSetStatus}
            />
          </S.OverviewMain>
          <S.OverviewRail>
            <SaldoBlock
              title={t('pages.paymentCalendar.saldo.title')}
              caption={label}
              opening={calendar?.openingBalancePln || 0}
              income={calendar?.totalIncomePln || 0}
              overdue={calendar?.totalOverduePln || 0}
              expense={calendar?.totalExpensePln || 0}
              closing={closing}
              received={calendar?.totalReceivedPln || 0}
              minBalance={calendar?.minBalancePln}
            />
            <CalendarCharts scopeDays={sortedDays} view={view} />
            <ToPayList
              days={sortedDays}
              onMoved={onRefetch}
              onMarkPaid={onMarkPaid}
              onSetStatus={onSetStatus}
            />
          </S.OverviewRail>
        </S.OverviewGrid>
      )}
    </>
  );

  const planningBody =
    loading && !calendar ? (
      <S.CenterLoader>
        <Spin />
      </S.CenterLoader>
    ) : (
      <S.Card>
        <PlanningBoard
          weekAnchor={weekAnchor}
          onWeekAnchorChange={setWeekAnchor}
          days={sortedDays}
          onEdit={onEditRow}
          onDelete={onDelete}
          onMoved={onRefetch}
          onMarkPaid={onMarkPaid}
          onSetStatus={onSetStatus}
        />
      </S.Card>
    );

  return (
    <MainTemplate>
      <S.Wrapper>
        <S.HeaderRow>
          <S.PageTitle>{t('pages.paymentCalendar.title')}</S.PageTitle>
          <S.HeaderActions>
            <FakturowniaSyncChip onSynced={onRefetch} />
            <Button onClick={() => setSettingsOpen(true)}>
              {t('pages.paymentCalendar.settings.title')}
            </Button>
            <Dropdown
              trigger={['click']}
              overlay={
                <Menu>
                  <Menu.Item key="expense" onClick={openCreateExpense}>
                    {t('pages.paymentCalendar.addExpense')}
                  </Menu.Item>
                  <Menu.Item key="income" onClick={openCreateIncome}>
                    {t('pages.paymentCalendar.addIncome')}
                  </Menu.Item>
                </Menu>
              }
            >
              <Button type="primary">
                {t('pages.paymentCalendar.addPayment')}
              </Button>
            </Dropdown>
          </S.HeaderActions>
        </S.HeaderRow>

        {calendar?.hasRateMissing && (
          <Alert
            type="warning"
            showIcon
            message={t('pages.paymentCalendar.rateMissingBanner')}
          />
        )}

        <Tabs activeKey={tab} onChange={setTab}>
          <Tabs.TabPane
            key="overview"
            tab={t('pages.paymentCalendar.tabs.overview')}
          >
            <S.TabBody>{overviewBody}</S.TabBody>
          </Tabs.TabPane>
          <Tabs.TabPane
            key="planning"
            tab={t('pages.paymentCalendar.tabs.planning')}
          >
            <S.TabBody>{planningBody}</S.TabBody>
          </Tabs.TabPane>
        </Tabs>

        <PlannedPaymentModal
          open={modalOpen}
          row={modalRow}
          createDirection="income"
          onClose={() => setModalOpen(false)}
          onSaved={refetch}
        />
        <SettingsDrawer
          open={settingsOpen}
          onClose={() => setSettingsOpen(false)}
          onChanged={refetch}
        />
      </S.Wrapper>
    </MainTemplate>
  );
});
